import * as SliderPrimitive from '@radix-ui/react-slider'
import { DollarSign } from 'lucide-react'
import { useState, useEffect } from 'react'

import { Input } from 'src/components/ui'
import { cn } from 'src/utils/cn'
import { formatCurrency, parseCurrencyToNumber } from 'src/utils/format-currency'

interface PriceRangeSliderProps {
  field: {
    value: number[]
    onChange: (value: number[]) => void
  }
  min: number
  max: number
  step: number
  className?: string
  disabled?: boolean
  formatMaxValue?: (value: number) => string | undefined
}

export const PriceRangeSlider = ({
  field,
  min,
  max,
  step,
  className,
  disabled,
  formatMaxValue,
}: PriceRangeSliderProps) => {
  const [localValue, setLocalValue] = useState<[number, number]>([min, max])

  useEffect(() => {
    if (Array.isArray(field.value) && field.value.length === 2) {
      setLocalValue(field.value as [number, number])
    }
  }, [field.value])

  const handleSliderChange = (newValue: number[]) => {
    const validValue: [number, number] = [newValue[0] || min, newValue[1] || max]
    setLocalValue(validValue)
    field.onChange(validValue)
  }

  const handleInputChange = (index: number, value: string) => {
    const numValue = parseCurrencyToNumber(value) || 0
    const newValue: [number, number] = [...localValue] as [number, number]
    newValue[index] = Math.max(min, Math.min(max, numValue))
    setLocalValue(newValue)
    field.onChange(newValue)
  }

  const formatValue = (value: number, index: number) => {
    if (index === 1 && formatMaxValue) {
      const formatted = formatMaxValue(value)
      if (formatted) return formatted
    }
    return formatCurrency(value)
  }

  return (
    <div className={cn('space-y-6', className)}>
      <div className="pt-2">
        <div className="relative flex w-full touch-none select-none items-center">
          <SliderPrimitive.Root
            min={min}
            max={max}
            step={step}
            value={localValue}
            disabled={disabled}
            onValueChange={handleSliderChange}
            className="relative flex w-full touch-none select-none items-center"
          >
            <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-accent">
              <SliderPrimitive.Range className="absolute h-full bg-secondary" />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb className="block size-5 cursor-pointer rounded-full bg-secondary outline outline-4 outline-white transition-colors disabled:pointer-events-none disabled:opacity-50" />
            <SliderPrimitive.Thumb className="block size-5 cursor-pointer rounded-full bg-secondary outline outline-4 outline-white transition-colors disabled:pointer-events-none disabled:opacity-50" />
          </SliderPrimitive.Root>
        </div>
      </div>
      <div className="flex items-center justify-between gap-8">
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" />
          <Input
            type="text"
            value={formatValue(localValue[0], 0)}
            disabled={disabled}
            onChange={(e) => handleInputChange(0, e.target.value.replace('$', ''))}
            className="w-full bg-accent pl-10 text-right disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <p>-</p>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 z-20 size-5 -translate-y-1/2 text-muted" />
          <Input
            type="text"
            value={formatValue(localValue[1], 1)}
            disabled={disabled}
            onChange={(e) => handleInputChange(1, e.target.value.replace('$', ''))}
            className="w-full bg-accent pl-10 text-right disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>
    </div>
  )
}
